import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Martini, CalendarDays, Search, Plus } from "lucide-react";
import { toast } from "sonner";
import { ExperienceCard } from "@/components/ExperienceCard";
import { EmptyState, ErrorState, Spinner } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, TYPE_LABELS } from "@/lib/api";

const CHIPS = [["", "All experiences"], ...Object.entries(TYPE_LABELS).filter(([k]) => k !== "other")];

export const useSaveToggle = (setList) => {
  const { setUser } = useAuth();
  return async (id) => {
    try {
      const { data } = await api.post(`/me/saved/${id}`);
      setList((l) => l.map((e) => (e.id === id ? { ...e, saved: data.saved.includes(id) } : e)));
      setUser((u) => ({ ...u, saved: data.saved }));
    } catch (e) {
      toast.error(apiError(e));
    }
  };
};

const SearchBar = ({ draft, setDraft, onSubmit }) => (
  <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} role="search" data-testid="discover-search-form"
    className="surface grid gap-px overflow-hidden bg-border p-0 md:grid-cols-[1.3fr_1fr_1fr_auto]">
    <label className="flex min-h-[60px] items-center gap-3 bg-card px-5">
      <MapPin className="h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
      <span className="sr-only">Destination</span>
      <input className="h-full w-full bg-transparent text-[15px] outline-none placeholder:text-muted-foreground" placeholder="Where would you like to go?" value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} data-testid="discover-destination-input" />
    </label>
    <label className="flex min-h-[60px] items-center gap-3 bg-card px-5">
      <Martini className="h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
      <span className="sr-only">Experience type</span>
      <select className="h-full w-full bg-transparent text-[15px] outline-none [&>option]:bg-card" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} data-testid="discover-type-select">
        <option value="">Experience type</option>
        {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </label>
    <div className="flex min-h-[60px] items-center gap-3 bg-card px-5">
      <CalendarDays className="h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
      <label className="sr-only" htmlFor="discover-month">Month</label>
      <input id="discover-month" type="month" className="w-full min-w-0 bg-transparent text-[15px] outline-none [color-scheme:dark]" value={draft.month} onChange={(e) => setDraft({ ...draft, month: e.target.value })} data-testid="discover-month-input" />
      <label className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
        <input type="checkbox" className="h-4 w-4 accent-[#20D69B]" checked={draft.flexible} onChange={(e) => setDraft({ ...draft, flexible: e.target.checked })} data-testid="discover-flexible-checkbox" /> Flexible
      </label>
    </div>
    <div className="bg-card p-2"><button type="submit" className="btn-primary h-full w-full min-w-[150px] text-base" data-testid="discover-explore-button"><Search className="h-4 w-4" /> Explore</button></div>
  </form>
);

export default function Discover() {
  const { user } = useAuth();
  const [draft, setDraft] = useState({ q: "", type: "", month: "", flexible: true });
  const [filters, setFilters] = useState(draft);
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const toggleSave = useSaveToggle(setList);

  const load = useCallback(async () => {
    setError("");
    setList(null);
    try {
      const { data } = await api.get("/experiences", { params: filters });
      setList(data);
    } catch (e) {
      setError(apiError(e));
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const pickType = (type) => { const f = { ...draft, type }; setDraft(f); setFilters(f); };

  return (
    <div data-testid="discover-page">
      <section className="relative overflow-hidden">
        <img src="/brand/angels-v2.webp" alt="" aria-hidden className="absolute right-0 top-0 hidden h-full w-[56%] object-cover object-[center_14%] md:block" />
        <div className="absolute inset-y-0 right-0 hidden w-[56%] bg-gradient-to-r from-background via-background/40 to-transparent md:block" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 hidden h-24 bg-gradient-to-t from-background md:block" aria-hidden />
        <div className="shell relative pb-10 pt-10 md:pb-14 md:pt-16">
          <p className="eyebrow mb-4">Members' area</p>
          <h1 className="font-display text-5xl leading-[0.95] lg:text-7xl"><span className="md:hidden">Discover</span><span className="hidden md:inline">Your next chapter<br />starts here.</span></h1>
          <p className="mt-4 text-lg text-foreground/85">Welcome, {user.name.split(" ")[0]}. Discover plans. Find your co-hosts. Make them yours.</p>
        </div>
      </section>
      <div className="shell space-y-6 pb-20">
        <SearchBar draft={draft} setDraft={setDraft} onSubmit={() => setFilters(draft)} />
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1" role="group" aria-label="Filter by type">
            {CHIPS.map(([k, label]) => (
              <button key={k || "all"} type="button" className="chip shrink-0" data-active={filters.type === k} aria-pressed={filters.type === k} onClick={() => pickType(k)} data-testid={`discover-chip-${k || "all"}`}>{label}</button>
            ))}
          </div>
          <p className="flex items-center gap-3 text-sm text-muted-foreground"><span className="h-px w-12 bg-foreground/30" />Illustrative concepts are labelled</p>
        </div>
        {error ? <ErrorState message={error} onRetry={load} /> : !list ? <Spinner label="Finding experiences" /> : list.length === 0 ? (
          <EmptyState title="Nothing matches yet." body="Try another destination or type, or propose the experience yourself."
            action={<Link to="/experiences/new" className="btn-primary" data-testid="empty-create-button"><Plus className="h-4 w-4" /> Create experience</Link>} />
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="discover-results">
            {list.map((e, i) => <ExperienceCard key={e.id} exp={e} index={i} onToggleSave={toggleSave} />)}
          </div>
        )}
      </div>
    </div>
  );
}
